const SCROLL_ROOT_SELECTOR = '.main-content'
/** Extra space below the fixed top nav so the anchored meal isn’t flush against it. */
const DEFAULT_EXTRA_INSET = 24
const HEADER_FALLBACK_PX = 56

/**
 * Reset the app scroll container to the top (Recipe Detail / overlays).
 * Body does not scroll — only `.main-content` does.
 * @returns {boolean} true if the scroll root was found
 */
export function scrollMainContentToTop() {
  if (typeof document === 'undefined') return false
  const scrollRoot = document.querySelector(SCROLL_ROOT_SELECTOR)
  if (!scrollRoot) return false
  if (typeof scrollRoot.scrollTo === 'function') {
    scrollRoot.scrollTo({ top: 0, behavior: 'auto' })
  } else {
    scrollRoot.scrollTop = 0
  }
  return true
}

/**
 * Bottom edge of the fixed top nav in viewport coords, or 0 if unavailable.
 * @returns {number}
 */
function readTopNavBottom() {
  if (typeof document === 'undefined') return 0
  const nav = document.querySelector('.top-nav')
  const bottom = nav?.getBoundingClientRect?.()?.bottom
  if (Number.isFinite(bottom) && bottom > 0) return bottom

  if (typeof window === 'undefined' || typeof getComputedStyle !== 'function') {
    return HEADER_FALLBACK_PX
  }
  const raw = getComputedStyle(document.documentElement)
    .getPropertyValue('--header-height')
    .trim()
  const parsed = Number.parseFloat(raw)
  // clamp()/calc() tokens don't parse as a single px value
  if (Number.isFinite(parsed) && !/clamp|calc|var\(/i.test(raw)) return parsed
  return HEADER_FALLBACK_PX
}

/**
 * Scroll `.main-content` so `el` sits just below the fixed top nav.
 * Uses synchronous scrollTop updates so a follow-up getBoundingClientRect is accurate.
 *
 * @param {Element | null | undefined} el
 * @param {{ topInset?: number, extraInset?: number, scrollRootSelector?: string }} [options]
 * @returns {boolean} true if a scroll root was found and adjusted (or already aligned)
 */
export function scrollElementToScrollRootTop(el, options = {}) {
  if (!el || typeof document === 'undefined') return false

  const selector = options.scrollRootSelector ?? SCROLL_ROOT_SELECTOR
  const scrollRoot = document.querySelector(selector)

  if (!scrollRoot) {
    if (typeof el.scrollIntoView === 'function') {
      el.scrollIntoView({ block: 'start', behavior: 'auto' })
    }
    return false
  }

  const rootRect = scrollRoot.getBoundingClientRect()
  const elRect = el.getBoundingClientRect()
  const extraInset = options.extraInset ?? DEFAULT_EXTRA_INSET
  const topInset = options.topInset
    ?? (Math.max(0, readTopNavBottom() - rootRect.top) + extraInset)
  const delta = elRect.top - rootRect.top - topInset
  if (Math.abs(delta) >= 1) {
    scrollRoot.scrollTop += delta
  }
  return true
}
